// ============================================================================
// Pay-day maths + pay-cycle helpers
// ----------------------------------------------------------------------------
// All date logic for pay day, pay cycles, and "safe to spend until pay day"
// lives here. UI components just call these helpers and trust the answer.
//
// Two key concepts:
//
//   - Nominal pay day = the day-of-month the user set in Settings (1-31)
//   - Actual pay day  = the date the user actually gets paid, after applying
//                       the "shift earlier on weekends" rule if enabled.
//
// Cycles are computed from ACTUAL pay days. So a cycle's start and end can
// each independently shift earlier by 1-2 days if pay day falls on a weekend.
//
// Bank holidays are NOT modelled - too region-specific and stale-prone for
// the value they'd add. Most bank holidays in the UK are weekend-adjacent
// (Easter, May, late August, Christmas/NY) and the weekend rule catches the
// majority of those by accident. Edge cases can be revisited later.
// ============================================================================

// Clamp a desired day-of-month to the actual last day of that month, so a
// pay_day of 31 resolves to 28/29/30 in shorter months.
function clampDay(year, monthIndex, day) {
  const lastDay = new Date(year, monthIndex + 1, 0).getDate()
  return Math.min(day, lastDay)
}

// If `date` falls on a weekend, walk back to the previous Friday. No-op on
// weekdays. Pure helper - caller decides whether to apply it.
function shiftBackToWeekday(date) {
  const dow = date.getDay() // 0=Sun, 6=Sat
  if (dow === 0) {
    // Sunday → Friday (back 2 days)
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 2)
  }
  if (dow === 6) {
    // Saturday → Friday (back 1 day)
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1)
  }
  return date
}

// Resolve the ACTUAL pay date for a given year + month, given a nominal pay
// day-of-month and whether the user's employer shifts weekends earlier.
// Returns a Date at midnight local. Internal helper, not exported.
function actualPayDateInMonth(year, monthIndex, payDay, shiftWeekends) {
  const nominal = new Date(year, monthIndex, clampDay(year, monthIndex, payDay))
  return shiftWeekends ? shiftBackToWeekday(nominal) : nominal
}

// Format a Date as a local-time ISO date string (YYYY-MM-DD) - never use
// toISOString() here, that's UTC and drifts on late-evening operations.
export function toIsoDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// ── Bill scheduling ───────────────────────────────────────
// A bill's frequency + anchor determines which months it occurs in. These
// helpers are the single source of truth for "does this bill land in month X".
//
// frequency values:
//   'monthly'         - every month (default; legacy bills)
//   'weekly'          - treated as monthly for occurrence (every month), but
//                       its monthly-equivalent cost is amount * 4.33
//   'annual'          - once a year, in anchor_month
//   'quarterly'       - every 3 months from anchor_month
//   'every_n_months'  - every interval_months from anchor_month
//   'one_off'         - once, in anchor_month of anchor_year
//
// monthIndex is 0-11 (JS month). anchor_month is 1-12 (human), normalised here.
export function billOccursInMonth(bill, year, monthIndex) {
  if (!bill) return false
  const freq = bill.frequency || 'monthly'
  if (freq === 'monthly' || freq === 'weekly') return true

  const anchor = bill.anchor_month ? bill.anchor_month - 1 : 0 // → 0-11

  if (freq === 'annual') return monthIndex === anchor

  if (freq === 'one_off') {
    if (!bill.anchor_year) return monthIndex === anchor // year unset → any year
    return year === bill.anchor_year && monthIndex === anchor
  }

  // For repeating multi-month schedules, work in ABSOLUTE months (year*12 +
  // month) so the pattern is continuous across year boundaries. The anchor sets
  // the phase. We anchor to a reference year well in the past so "from <month>"
  // lands correctly and Nov→Jan wrapping works. anchor_year is only meaningful
  // for one_off, so for these we phase purely on anchor month.
  const absMonth = year * 12 + monthIndex
  const anchorAbs = (bill.anchor_year || 2000) * 12 + anchor

  if (freq === 'quarterly') {
    const diff = absMonth - anchorAbs
    return diff >= 0 && diff % 3 === 0
  }

  if (freq === 'every_n_months') {
    const n = bill.interval_months && bill.interval_months > 0 ? bill.interval_months : 1
    const diff = absMonth - anchorAbs
    return diff >= 0 && diff % n === 0
  }

  return true
}

// The actual Date a bill is due within a given month, or null if it doesn't
// occur that month. Clamps due_day to the month length.
export function billDueDateInMonth(bill, year, monthIndex) {
  if (!billOccursInMonth(bill, year, monthIndex)) return null
  const day = clampDay(year, monthIndex, bill.due_day || 1)
  return new Date(year, monthIndex, day)
}

// Monthly-equivalent cost of a bill, for "what does this cost per month on
// average" style totals. Annual / quarterly / every-N are amortised.
export function billMonthlyEquivalent(bill) {
  if (!bill) return 0
  const amt = bill.amount || 0
  switch (bill.frequency) {
    case 'annual': return amt / 12
    case 'quarterly': return amt / 3
    case 'weekly': return amt * 4.33
    case 'every_n_months': {
      const n = bill.interval_months && bill.interval_months > 0 ? bill.interval_months : 1
      return amt / n
    }
    case 'one_off': return 0 // not a recurring monthly cost
    default: return amt // monthly
  }
}

// Annualised cost of a bill (what it costs across a full year).
export function billAnnualCost(bill) {
  if (!bill) return 0
  const amt = bill.amount || 0
  switch (bill.frequency) {
    case 'annual': return amt
    case 'quarterly': return amt * 4
    case 'weekly': return amt * 52
    case 'every_n_months': {
      const n = bill.interval_months && bill.interval_months > 0 ? bill.interval_months : 1
      return amt * (12 / n)
    }
    case 'one_off': return amt // counts once in its year
    default: return amt * 12 // monthly
  }
}

// ── Income scheduling ─────────────────────────────────────
// Mirrors bill scheduling logic for variable income entries.
// Income frequencies: monthly | quarterly | annual | every_n_months
// (No weekly - income isn't paid weekly in this model.)

export function incomeOccursInMonth(entry, year, monthIndex) {
  // Reuses the same phase/anchor logic as billOccursInMonth.
  // entry shape: { frequency, anchor_month, anchor_year, interval_months }
  return billOccursInMonth(entry, year, monthIndex)
}

// Amortised monthly equivalent for a variable income entry.
// Used for the "monthly recurring" baseline and income charts.
export function incomeMonthlyEquivalent(entry) {
  if (!entry) return 0
  const amt = entry.amount || 0
  switch (entry.frequency) {
    case 'annual':        return amt / 12
    case 'quarterly':     return amt / 3
    case 'every_n_months': {
      const n = entry.interval_months && entry.interval_months > 0 ? entry.interval_months : 1
      return amt / n
    }
    default:              return amt  // monthly
  }
}

// ── Pay days ──────────────────────────────────────────────

// The next ACTUAL pay date on or after `from` (default today).
export function nextPayDay(payDay, from = new Date(), shiftWeekends = true) {
  if (!payDay) return null
  const today = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const y = from.getFullYear()
  const m = from.getMonth()
  // Try this month first.
  let candidate = actualPayDateInMonth(y, m, payDay, shiftWeekends)
  if (candidate >= today) return candidate
  // Otherwise it's next month.
  return actualPayDateInMonth(y, m + 1, payDay, shiftWeekends)
}

// Whole days from `from` until the next pay day (0 = pay day is today).
export function daysUntilPayDay(payDay, from = new Date(), shiftWeekends = true) {
  const next = nextPayDay(payDay, from, shiftWeekends)
  if (!next) return null
  const today = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  return Math.round((next - today) / (1000 * 60 * 60 * 24))
}

// Will a bill due on `dueDay` (day-of-month) fall on or before the next pay day,
// counting from today? Those are the bills that must be covered from the current
// pot; bills after pay day are covered by the next pay packet.
// Accepts a full bill object so it can respect variable schedules (annual,
// every-N, one-off) - a bill that doesn't occur in the relevant month returns
// false. NOTE: due dates are NOT weekend-shifted - banks handle that themselves.
export function billDueBeforePayDay(bill, payDay, from = new Date(), shiftWeekends = true) {
  if (!bill || !bill.due_day) return false
  const next = nextPayDay(payDay, from, shiftWeekends)
  if (!next) return false
  const today = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  // This bill's next occurrence on/after today, checking this month then next.
  const y = from.getFullYear(); const m = from.getMonth()
  let occ = billDueDateInMonth(bill, y, m)
  if (!occ || occ < today) {
    const nm = new Date(y, m + 1, 1)
    occ = billDueDateInMonth(bill, nm.getFullYear(), nm.getMonth())
  }
  if (!occ) return false
  return occ >= today && occ <= next
}

// ── Cycles ────────────────────────────────────────────────

// A pay cycle is bounded by two ACTUAL pay dates. The cycle that contains
// `from` (default today): starts on the most recent pay day on or before
// `from`, ends on the day BEFORE the next pay day (so cycles don't overlap).
//
// Returns { start: Date, end: Date, startIso: string, endIso: string, label: string }
// where label is the "end-month" name (e.g. "May 2026") matching the
// user-chosen convention: a cycle that LEADS INTO 28 May pay day is "May".
export function getPayCycle(payDay, from = new Date(), shiftWeekends = true) {
  if (!payDay) return null
  const today = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const y = from.getFullYear()
  const m = from.getMonth()

  // The pay day that OPENED the cycle we're in: the most recent actual pay
  // date on or before today.
  const thisMonthsPay = actualPayDateInMonth(y, m, payDay, shiftWeekends)
  let start
  if (thisMonthsPay <= today) {
    start = thisMonthsPay
  } else {
    start = actualPayDateInMonth(y, m - 1, payDay, shiftWeekends)
  }

  // The next pay date AFTER `start` - which closes this cycle.
  // Compute by looking at next month relative to `start`.
  const nextPay = actualPayDateInMonth(start.getFullYear(), start.getMonth() + 1, payDay, shiftWeekends)
  // Cycle ends the day BEFORE the next pay date (the shifted one).
  const end = new Date(nextPay.getFullYear(), nextPay.getMonth(), nextPay.getDate() - 1)

  // The label uses the END-MONTH (Option 2 from design discussion). Pay day
  // leading INTO May is "May".
  const labelDate = nextPay // i.e. when pay-day-for-May actually lands
  const label = labelDate.toLocaleString('en-GB', { month: 'long', year: 'numeric' })

  return {
    start,
    end,
    startIso: toIsoDate(start),
    endIso: toIsoDate(end),
    label,
  }
}

// Get the cycle whose END falls in the given calendar month - i.e. the cycle
// labelled "May 2026" is the one whose pay-day-leading-in is in May.
// `monthKey` = 'YYYY-MM' (e.g. '2026-05'). Used for cycle navigation by name.
export function getCycleByEndMonth(monthKey, payDay, shiftWeekends = true) {
  if (!payDay || !monthKey) return null
  const [yStr, mStr] = monthKey.split('-')
  const y = parseInt(yStr, 10)
  const m = parseInt(mStr, 10) - 1
  // The pay day landing in this month closes our target cycle.
  const closingPay = actualPayDateInMonth(y, m, payDay, shiftWeekends)
  const end = new Date(closingPay.getFullYear(), closingPay.getMonth(), closingPay.getDate() - 1)
  // The pay day OPENING this cycle is the previous month's actual pay day.
  const start = actualPayDateInMonth(y, m - 1, payDay, shiftWeekends)
  return {
    start,
    end,
    startIso: toIsoDate(start),
    endIso: toIsoDate(end),
    label: closingPay.toLocaleString('en-GB', { month: 'long', year: 'numeric' }),
  }
}

// Step one cycle forward or backward from a given cycle. Used by the cycle
// picker arrows. Returns the same shape as getPayCycle.
export function shiftCycle(cycle, direction, payDay, shiftWeekends = true) {
  if (!cycle || !payDay) return null
  // To move forward: anchor on a date inside the NEXT cycle (one day after
  // current cycle's end is the next pay day). To move back: anchor on the
  // day BEFORE current start.
  const anchor = direction > 0
    ? new Date(cycle.end.getFullYear(), cycle.end.getMonth(), cycle.end.getDate() + 1)
    : new Date(cycle.start.getFullYear(), cycle.start.getMonth(), cycle.start.getDate() - 1)
  return getPayCycle(payDay, anchor, shiftWeekends)
}

// True if a YYYY-MM-DD date string falls within the given cycle.
export function isDateInCycle(isoDate, cycle) {
  if (!isoDate || !cycle) return false
  return isoDate >= cycle.startIso && isoDate <= cycle.endIso
}

// A short human-readable date range for the cycle, e.g. "28 Apr - 27 May".
export function cycleRangeLabel(cycle) {
  if (!cycle) return ''
  const startStr = cycle.start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  const endStr = cycle.end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  return `${startStr} - ${endStr}`
}

// ── Safe-to-spend (the headline figure) ───────────────────

// spendableBalance is money you can actually spend (current accounts - NOT
// emergency fund or savings goals). billsBeforePayDay is the sum of bills due
// on/before the next pay day. quickSpent (optional) is the sum of quick-spend
// entries logged in the current cycle and is subtracted alongside committed
// bills. Returns the safe-to-spend pot and a per-day figure.
export function safeToSpend({ spendableBalance, billsBeforePayDay, quickSpent = 0, payDay, from = new Date(), shiftWeekends = true }) {
  const days = daysUntilPayDay(payDay, from, shiftWeekends)
  const committed = billsBeforePayDay || 0
  const spent = quickSpent || 0
  const pot = Math.max(0, (spendableBalance || 0) - committed - spent)
  const perDay = days && days > 0 ? pot / days : pot
  return {
    pot,
    perDay,
    days,
    committed,
    spent,
    shortfall: (spendableBalance || 0) - committed - spent < 0,
  }
}

// Build an array of cycles for the cycle picker. By default this is centred on
// the current cycle: a couple of FUTURE cycles (so users can pre-plan next
// month's budget), the current cycle, and a handful of recent past cycles.
// New users therefore don't see a year of empty history in the dropdown.
//
// `opts` = { forward = 2, back = 3 }. Returns newest→oldest (future cycles
// first, then current, then past), each with a `rangeLabel` for display.
export function cyclesForPicker(payDay, shiftWeekends = true, opts = {}, from = new Date()) {
  if (!payDay) return []
  const forward = opts.forward != null ? opts.forward : 2
  const back = opts.back != null ? opts.back : 3

  const current = getPayCycle(payDay, from, shiftWeekends)
  if (!current) return []

  const out = []
  // Future cycles, furthest-first so the list reads future→present→past.
  let fwd = current
  const futures = []
  for (let i = 0; i < forward; i++) {
    fwd = shiftCycle(fwd, 1, payDay, shiftWeekends)
    if (fwd) futures.push({ ...fwd, rangeLabel: cycleRangeLabel(fwd) })
  }
  out.push(...futures.reverse())
  // Current.
  out.push({ ...current, rangeLabel: cycleRangeLabel(current) })
  // Past cycles.
  let bwd = current
  for (let i = 0; i < back; i++) {
    bwd = shiftCycle(bwd, -1, payDay, shiftWeekends)
    if (bwd) out.push({ ...bwd, rangeLabel: cycleRangeLabel(bwd) })
  }
  return out
}

// ── Account funding ───────────────────────────────────────
// Given accounts and bills, work out how much each account needs to cover the
// bills assigned to it that fall due before the next pay day. Returns one row
// per account that has assigned bills, plus an optional "unassigned" bucket.
//
//   per row: {
//     accountId, accountName, ownerLabel,
//     balance,                 // current balance
//     billsDue,                // sum of assigned bills due before next pay day
//     topUp,                   // max(0, billsDue - balance) - the actionable number
//     covered,                 // true if balance already covers billsDue
//   }
//
// Bills with no account_id are summed into `unassigned` (billsDue only - no
// balance to compare against).
export function accountFunding({ accounts, bills, payDay, from = new Date(), shiftWeekends = true, includeAllCurrent = false }) {
  const rows = []
  let unassignedDue = 0

  // Funding is a per-cycle question: "which bills fall due during the current
  // pay cycle, so I can make sure each account can cover them?" We use the
  // current cycle window (start..end) rather than "before next pay day",
  // because on pay day itself the latter collapses to a zero-length window.
  const cycle = getPayCycle(payDay, from, shiftWeekends)
  if (!cycle) {
    return { rows: [], owners: [], unassignedDue: 0, totalTopUp: 0, hasAssignedBills: false }
  }

  // A bill counts if it OCCURS in the relevant month and its due date lands in
  // the cycle window. Respects variable schedules (annual, every-N, one-off).
  const billDueInCycle = (bill) => {
    if (!bill.due_day) return false
    const s = cycle.start
    for (const probe of [s, new Date(s.getFullYear(), s.getMonth() + 1, 1)]) {
      const occ = billDueDateInMonth(bill, probe.getFullYear(), probe.getMonth())
      if (occ && occ >= cycle.start && occ <= cycle.end) return true
    }
    return false
  }

  const dueBills = (bills || []).filter(b => billDueInCycle(b))

  const dueByAccount = {}
  for (const b of dueBills) {
    if (b.account_id) {
      dueByAccount[b.account_id] = (dueByAccount[b.account_id] || 0) + (b.amount || 0)
    } else {
      unassignedDue += b.amount || 0
    }
  }

  for (const acc of accounts || []) {
    const billsDue = dueByAccount[acc.id] || 0
    // When includeAllCurrent is set, surface every account passed in even if it
    // has no bills (so the dashboard can show balances at a glance). Otherwise
    // only surface accounts that actually have bills due.
    if (billsDue <= 0 && !includeAllCurrent) continue
    const balance = acc.balance || 0
    const topUp = Math.max(0, billsDue - balance)
    rows.push({
      accountId: acc.id,
      accountName: acc.name,
      ownerLabel: acc.owner_label || 'Joint',
      balance,
      billsDue,
      topUp,
      covered: balance >= billsDue,
      hasBills: billsDue > 0,
    })
  }

  const byOwner = {}
  for (const r of rows) {
    if (!byOwner[r.ownerLabel]) byOwner[r.ownerLabel] = { ownerLabel: r.ownerLabel, topUp: 0, billsDue: 0, balance: 0, accounts: [] }
    byOwner[r.ownerLabel].topUp += r.topUp
    byOwner[r.ownerLabel].billsDue += r.billsDue
    byOwner[r.ownerLabel].balance += r.balance
    byOwner[r.ownerLabel].accounts.push(r)
  }

  return {
    rows,
    owners: Object.values(byOwner),
    unassignedDue,
    totalTopUp: rows.reduce((s, r) => s + r.topUp, 0),
    hasAssignedBills: rows.some(r => r.hasBills),
  }
}

// ── Backwards-compatible cycle-start helper ───────────────
// Kept for quick_spends and any other code that just wants the ISO date string
// of the current cycle's start. Delegates to getPayCycle.
export function currentPayCycleStart(payDay, from = new Date(), shiftWeekends = true) {
  const c = getPayCycle(payDay, from, shiftWeekends)
  return c ? c.startIso : null
}
